import type { ComponentType } from "react";
import { useTranslation } from "react-i18next";
import { Check, Clock, Copy, CornerDownRight, Info, Square, type LucideProps } from "lucide-react";
import type { AskNexusAnswerBlock, AskNexusFollowUpsCard, AskNexusTurn } from "@/features/ask-nexus/api/ask-nexus.api";
import { AskNexusAlert } from "@/features/ask-nexus/components/AskNexusAlert";
import { AskNexusCardUnavailable, AskNexusComparisonTable, AskNexusKpiTiles } from "@/features/ask-nexus/components/AskNexusCards";
import { AskNexusClarification, type ClarificationReply } from "@/features/ask-nexus/components/AskNexusClarification";
import { AskNexusMarkdown } from "@/features/ask-nexus/components/AskNexusMarkdown";
import { AskNexusNexusLink, useCanOpenNexusLink } from "@/features/ask-nexus/components/AskNexusNexusLink";
import { AskNexusCreativeExamples, AskNexusOpportunities } from "@/features/ask-nexus/components/AskNexusRecordCards";
import { AskNexusReportRequest } from "@/features/ask-nexus/components/AskNexusReportCard";
import { AskNexusOpportunitySuggestion, AskNexusScript } from "@/features/ask-nexus/components/AskNexusSuggestionCards";
import { AskNexusToolActivity } from "@/features/ask-nexus/components/AskNexusToolActivity";
import { AskNexusWorking } from "@/features/ask-nexus/components/AskNexusWorking";
import { answerCopyText } from "@/features/ask-nexus/lib/card-format";
import { nextStepLinks } from "@/features/ask-nexus/lib/nexus-links";
import { toolLabel } from "@/features/ask-nexus/lib/tool-labels";
import { ASK_NEXUS_ICON_STROKE } from "@/features/ask-nexus/lib/ui";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";

export function AskNexusUserMessage({ text }: { text: string }) {
  return (
    <div className="flex flex-col items-end gap-2">
      <p className="max-w-[86%] rounded-[20px] bg-(--an-inset) px-4 py-2.5 text-sm leading-[1.55] whitespace-pre-wrap">{text}</p>
    </div>
  );
}

/**
 * One question and everything the agent did for it. `reply` answers a
 * clarification card in this turn. `onFollowUp` is set only for the latest
 * answer while a question can be sent; without it, suggested questions stay hidden.
 */
export function AskNexusTurnView({
  turn,
  reply,
  onFollowUp,
}: {
  turn: AskNexusTurn;
  reply: ClarificationReply;
  onFollowUp?: (question: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const isRunning = turn.status === "running";
  const blocks: AskNexusAnswerBlock[] = turn.blocks ?? (turn.answer ? [{ type: "markdown", text: turn.answer }] : []);
  const runningTool = turn.toolActivity.find((item) => item.status === "running");

  return (
    <div className="mb-6" data-testid="ask-nexus-turn">
      {turn.message ? (
        <div className="mb-6">
          <AskNexusUserMessage text={turn.message} />
        </div>
      ) : null}
      <div className="flex flex-col gap-4">
        <AskNexusToolActivity activity={turn.toolActivity} isRunning={isRunning} />
        {blocks.map((block, index) => (
          <AnswerBlock key={index} block={block} reply={reply} />
        ))}
        {isRunning ? (
          <AskNexusWorking
            text={runningTool ? `${toolLabel(t, runningTool.tool)}…` : t("askNexus.turn.working", "Working on your answer")}
          />
        ) : null}
        {isRunning ? null : <NextSteps blocks={blocks} />}
        <TurnOutcome turn={turn} />
        {blocks.length > 0 && !isRunning ? <AnswerActions text={answerCopyText(t, turn, i18n.language)} /> : null}
        {onFollowUp && turn.status === "answered" ? <FollowUpQuestions blocks={blocks} onAsk={onFollowUp} /> : null}
      </div>
    </div>
  );
}

function AnswerBlock({ block, reply }: { block: AskNexusAnswerBlock; reply: ClarificationReply }) {
  if (block.type === "markdown") return <AskNexusMarkdown text={block.text} />;
  if (block.type === "card_unavailable") return <AskNexusCardUnavailable reason={block.reason} />;
  switch (block.card.card) {
    case "kpi_tiles":
      return <AskNexusKpiTiles card={block.card} />;
    case "comparison_table":
      return <AskNexusComparisonTable card={block.card} />;
    case "creative_examples":
      return <AskNexusCreativeExamples card={block.card} />;
    case "opportunities":
      return <AskNexusOpportunities card={block.card} />;
    case "script":
      return <AskNexusScript card={block.card} />;
    case "opportunity_suggestion":
      return <AskNexusOpportunitySuggestion card={block.card} />;
    case "clarification":
      return <AskNexusClarification card={block.card} reply={reply} />;
    case "link":
      // Shown once, with the other next steps, after the answer.
      return null;
    case "follow_ups":
      // Shown after the answer, and only while they can be sent.
      return null;
    case "report_request":
      return <AskNexusReportRequest card={block.card} />;
  }
}

/**
 * Nexus pages where the person can continue, after the answer: buttons that
 * open a page, apart from the clarification choices that send a reply. Each
 * shows only where the chat's organization grants its page; none shows, none
 * takes space.
 */
function NextSteps({ blocks }: { blocks: AskNexusAnswerBlock[] }) {
  const { t } = useTranslation();
  const canOpen = useCanOpenNexusLink();
  const links = nextStepLinks(blocks, canOpen);
  if (links.length === 0) return null;
  return (
    <div role="group" aria-label={t("askNexus.links.nextSteps", "Next steps in Nexus")} className="flex flex-wrap gap-2" data-testid="ask-nexus-next-steps">
      {links.map((link) => (
        <AskNexusNexusLink key={JSON.stringify(link)} link={link} />
      ))}
    </div>
  );
}

/**
 * Questions the agent suggested asking next. Each sends as the next message of
 * this chat, as if typed: the normal follow-up, with its own checks. They read
 * as replies (filled rows), apart from the outlined buttons that open a page.
 * An answer that asks a clarifying question offers none: its card is the reply.
 */
function FollowUpQuestions({ blocks, onAsk }: { blocks: AskNexusAnswerBlock[]; onAsk: (question: string) => void }) {
  const { t } = useTranslation();
  const followUps = blocks.find((block): block is { type: "card"; card: AskNexusFollowUpsCard } => block.type === "card" && block.card.card === "follow_ups");
  const asksClarification = blocks.some((block) => block.type === "card" && block.card.card === "clarification");
  if (!followUps || asksClarification) return null;
  return (
    <div role="group" aria-label={t("askNexus.followUps.label", "Suggested follow-up questions")} className="flex flex-col items-start gap-1.5" data-testid="ask-nexus-follow-ups">
      {followUps.card.questions.map((question) => (
        <button
          key={question}
          type="button"
          className="inline-flex max-w-full items-start gap-2 rounded-2xl bg-(--an-inset) px-3 py-1.5 text-left text-[13px] leading-snug text-(--an-ink) transition-colors hover:bg-(--an-neutral-tint) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--an-ink-3)"
          onClick={() => onAsk(question)}
        >
          <CornerDownRight className="mt-[3px] size-3.5 flex-none text-(--an-ink-3)" strokeWidth={ASK_NEXUS_ICON_STROKE} aria-hidden="true" />
          <span>{question}</span>
        </button>
      ))}
    </div>
  );
}

function TurnOutcome({ turn }: { turn: AskNexusTurn }) {
  const { t } = useTranslation();
  if (turn.status === "stopped") {
    return <Note icon={Square} text={t("askNexus.turn.stopped", "Stopped. Ask again to start over.")} />;
  }
  if (turn.status === "timed_out") {
    return <Note icon={Clock} text={t("askNexus.turn.timedOut", "This answer took too long and was stopped. Try a narrower question.")} />;
  }
  if (turn.status === "declined") {
    return <Note icon={Info} text={t("askNexus.turn.declined", "Ask Nexus couldn't answer that question. Try asking it differently.")} />;
  }
  if (turn.status === "failed" || turn.errors.length > 0) {
    return (
      <AskNexusAlert
        tone="bad"
        title={t("askNexus.turn.failedTitle", "Ask Nexus couldn't finish this answer")}
        description={t("askNexus.turn.failedDescription", "Something went wrong on our side, and nothing was changed. Ask again in a few minutes.")}
      />
    );
  }
  return null;
}

function Note({ icon: NoteIcon, text }: { icon: ComponentType<LucideProps>; text: string }) {
  return (
    <p className="flex items-start gap-2 rounded-xl bg-(--an-inset) px-3 py-2.5 text-xs text-(--an-ink-2)">
      <NoteIcon className="mt-px size-3.5 flex-none" strokeWidth={ASK_NEXUS_ICON_STROKE} aria-hidden="true" />
      {text}
    </p>
  );
}

/** Copy takes the answer with its cards written out as text, never a card directive. */
function AnswerActions({ text }: { text: string }) {
  const { t } = useTranslation();
  const { copied, copy } = useCopyToClipboard();
  const label = copied ? t("askNexus.turn.copied", "Copied") : t("askNexus.turn.copy", "Copy answer");
  const ActionIcon = copied ? Check : Copy;
  return (
    <div className="-mt-2 -ml-2 flex gap-0.5">
      <button
        type="button"
        className="grid size-7 place-items-center rounded-full text-(--an-ink-3) transition-colors hover:bg-(--an-inset-hover) hover:text-(--an-ink)"
        aria-label={label}
        title={label}
        onClick={() => void copy(text)}
      >
        <ActionIcon className="size-3.5" strokeWidth={ASK_NEXUS_ICON_STROKE} aria-hidden="true" />
      </button>
    </div>
  );
}
